import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";

import { crmService } from "@/services/crmService";
import { RELATIONSHIP_TYPES } from "@/config/crmOptions";
import { CONTACT_METHODS, US_STATES } from "@/config/formOptions";
import { Field } from "@/components/profile/FormControls";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const emptyContact = {
  first_name: "", last_name: "", email: "", phone: "", relationship_type: "lead",
  lead_source_id: "", tag_ids: [], birthday: "", preferred_contact_method: "",
  address_line1: "", address_line2: "", city: "", state: "", postal_code: "",
  notes_summary: "",
};

const NONE = "__none__";

/** Reusable contact form body (used by Add + Edit dialogs). */
export const ContactForm = ({ form, set, errors, idPrefix = "cf" }) => {
  const [sources, setSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    crmService.listSources().then(setSources).catch(() => {});
    crmService.listTags().then(setTags).catch(() => {});
  }, []);

  const toggleTag = (id) => {
    set("tag_ids", form.tag_ids.includes(id) ? form.tag_ids.filter((t) => t !== id) : [...form.tag_ids, id]);
  };

  const addTag = async () => {
    const name = newTag.trim();
    if (!name) return;
    try {
      const tag = await crmService.createTag(name);
      setTags((t) => (t.find((x) => x.id === tag.id) ? t : [...t, tag]));
      if (!form.tag_ids.includes(tag.id)) set("tag_ids", [...form.tag_ids, tag.id]);
      setNewTag("");
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" htmlFor={`${idPrefix}-first`} error={errors.first_name}>
          <Input id={`${idPrefix}-first`} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="contact-first-name" />
        </Field>
        <Field label="Last name" htmlFor={`${idPrefix}-last`} error={errors.last_name}>
          <Input id={`${idPrefix}-last`} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="contact-last-name" />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email" htmlFor={`${idPrefix}-email`} error={errors.email}>
          <Input id={`${idPrefix}-email`} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" placeholder="name@example.com" />
        </Field>
        <Field label="Phone" htmlFor={`${idPrefix}-phone`} error={errors.phone}>
          <Input id={`${idPrefix}-phone`} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" placeholder="(555) 555-1234" />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Relationship" htmlFor={`${idPrefix}-rel`} error={errors.relationship_type}>
          <Select value={form.relationship_type || "lead"} onValueChange={(v) => set("relationship_type", v)}>
            <SelectTrigger id={`${idPrefix}-rel`} data-testid="contact-relationship"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Lead source" htmlFor={`${idPrefix}-source`} error={errors.lead_source_id}>
          <Select value={form.lead_source_id || NONE} onValueChange={(v) => set("lead_source_id", v === NONE ? "" : v)}>
            <SelectTrigger id={`${idPrefix}-source`} data-testid="contact-source"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>No source</SelectItem>
              {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Tags" error={errors.tag_ids}>
        <div className="space-y-2" data-testid="contact-tags">
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const active = form.tag_ids.includes(t.id);
              return (
                <button key={t.id} type="button" onClick={() => toggleTag(t.id)} data-testid={`contact-tag-${t.slug}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "border-brand-navy bg-brand-navy text-white" : "border-border bg-background text-foreground hover:bg-secondary"}`}>
                  {t.name}
                </button>
              );
            })}
            {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags yet — create one below.</span>}
          </div>
          <div className="flex items-center gap-2">
            <Input value={newTag} onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="New tag…" className="h-8 w-40 text-sm" data-testid="contact-new-tag-input" />
            <button type="button" onClick={addTag} data-testid="contact-add-tag"
              className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        </div>
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Preferred contact" htmlFor={`${idPrefix}-pcm`} error={errors.preferred_contact_method}>
          <Select value={form.preferred_contact_method || NONE} onValueChange={(v) => set("preferred_contact_method", v === NONE ? "" : v)}>
            <SelectTrigger id={`${idPrefix}-pcm`} data-testid="contact-pcm"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>No preference</SelectItem>
              {CONTACT_METHODS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Birthday (optional)" htmlFor={`${idPrefix}-bday`} error={errors.birthday} hint="YYYY-MM-DD">
          <Input id={`${idPrefix}-bday`} type="date" value={form.birthday || ""} onChange={(e) => set("birthday", e.target.value)} data-testid="contact-birthday" />
        </Field>
      </div>

      <details className="rounded-lg border border-border bg-secondary/30 px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium text-foreground">Address (optional)</summary>
        <div className="mt-3 space-y-4">
          <Field label="Address line 1" htmlFor={`${idPrefix}-a1`} error={errors.address_line1}>
            <Input id={`${idPrefix}-a1`} value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} data-testid="contact-address1" />
          </Field>
          <Field label="Address line 2" htmlFor={`${idPrefix}-a2`} error={errors.address_line2}>
            <Input id={`${idPrefix}-a2`} value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} data-testid="contact-address2" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="City" htmlFor={`${idPrefix}-city`} error={errors.city}>
              <Input id={`${idPrefix}-city`} value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="contact-city" />
            </Field>
            <Field label="State" htmlFor={`${idPrefix}-state`} error={errors.state}>
              <Select value={form.state || NONE} onValueChange={(v) => set("state", v === NONE ? "" : v)}>
                <SelectTrigger id={`${idPrefix}-state`} data-testid="contact-state"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value={NONE}>—</SelectItem>
                  {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="ZIP" htmlFor={`${idPrefix}-zip`} error={errors.postal_code}>
              <Input id={`${idPrefix}-zip`} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="contact-zip" />
            </Field>
          </div>
        </div>
      </details>

      <Field label="Summary note (optional)" htmlFor={`${idPrefix}-summary`} error={errors.notes_summary}>
        <Textarea id={`${idPrefix}-summary`} rows={2} value={form.notes_summary} onChange={(e) => set("notes_summary", e.target.value)} data-testid="contact-summary" placeholder="Quick context about this person…" />
      </Field>
    </div>
  );
};

/** Normalize a form object into a clean API payload. */
export const contactPayload = (form) => ({
  ...form,
  email: form.email || null,
  phone: form.phone || null,
  lead_source_id: form.lead_source_id || null,
  preferred_contact_method: form.preferred_contact_method || null,
  birthday: form.birthday || null,
  address_line1: form.address_line1 || null,  address_line2: form.address_line2 || null,
  city: form.city || null,
  state: form.state || null,
  postal_code: form.postal_code || null,
  notes_summary: form.notes_summary || null,
});
